/**
 * Open5e API Client
 *
 * Fetches D&D 5e SRD data from the Open5e API.
 * Includes in-memory caching to avoid repeated requests.
 */
// =============================================================================
// CONFIG
// =============================================================================
const API_BASE_V1 = 'https://api.open5e.com/v1';
const DEFAULT_DOCUMENT = 'wotc-srd'; // SRD content only
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
const cache = new Map();
function getCached(key) {
    const entry = cache.get(key);
    if (!entry)
        return null;
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
        cache.delete(key);
        return null;
    }
    return entry.data;
}
function setCache(key, data) {
    cache.set(key, { data, timestamp: Date.now() });
}
export function clearOpen5eCache() {
    cache.clear();
}
// =============================================================================
// FETCH HELPERS
// =============================================================================
async function fetchJson(url) {
    const cached = getCached(url);
    if (cached)
        return cached;
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Open5e API error: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    setCache(url, data);
    return data;
}
async function fetchAllPages(baseUrl) {
    const cacheKey = `all:${baseUrl}`;
    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp <= CACHE_TTL_MS) {
        return cached.data;
    }
    const results = [];
    let url = baseUrl;
    while (url) {
        const response = await fetchJson(url);
        results.push(...response.results);
        url = response.next;
    }
    setCache(cacheKey, results);
    return results;
}
// =============================================================================
// RACES
// =============================================================================
export async function getRaces(documentSlug = DEFAULT_DOCUMENT) {
    const url = `${API_BASE_V1}/races/?document__slug=${documentSlug}`;
    return fetchAllPages(url);
}
export async function getRace(slug) {
    const url = `${API_BASE_V1}/races/${slug}/`;
    return fetchJson(url);
}
// =============================================================================
// CLASSES
// =============================================================================
export async function getClasses(documentSlug = DEFAULT_DOCUMENT) {
    const url = `${API_BASE_V1}/classes/?document__slug=${documentSlug}`;
    return fetchAllPages(url);
}
export async function getClass(slug) {
    const url = `${API_BASE_V1}/classes/${slug}/`;
    return fetchJson(url);
}
// =============================================================================
// BACKGROUNDS
// =============================================================================
export async function getBackgrounds(documentSlug = DEFAULT_DOCUMENT) {
    const url = `${API_BASE_V1}/backgrounds/?document__slug=${documentSlug}`;
    return fetchAllPages(url);
}
export async function getBackground(slug) {
    const url = `${API_BASE_V1}/backgrounds/${slug}/`;
    return fetchJson(url);
}
// Transform v1 spell to add computed helper fields
function normalizeSpell(spell) {
    return {
        ...spell,
        // Add helpers for compatibility with UI components
        verbal: spell.requires_verbal_components,
        somatic: spell.requires_somatic_components,
        material_specified: spell.material,
        classes: spell.dnd_class ? spell.dnd_class.split(', ').map(c => c.trim()) : [],
    };
}
export async function getSpells(filter = {}) {
    const params = new URLSearchParams();
    // null = search all documents; undefined = SRD default; string = specific document
    if (filter.documentSlug === null) { /* no document filter — search everything */ }
    else if (filter.documentSlug)
        params.set('document__slug', filter.documentSlug);
    else
        params.set('document__slug', DEFAULT_DOCUMENT);
    if (filter.level !== undefined)
        params.set('level_int', String(filter.level));
    if (filter.school)
        params.set('school', filter.school);
    if (filter.className)
        params.set('dnd_class__icontains', filter.className);
    if (filter.ritual === true)
        params.set('ritual', 'yes');
    if (filter.concentration === true)
        params.set('concentration', 'yes');
    if (filter.search)
        params.set('search', filter.search);
    if (filter.limit)
        params.set('limit', String(filter.limit));
    const url = `${API_BASE_V1}/spells/?${params.toString()}`;
    const spells = await fetchAllPages(url);
    return spells.map(normalizeSpell);
}
export async function getSpell(slug) {
    const url = `${API_BASE_V1}/spells/${slug}/`;
    const spell = await fetchJson(url);
    return normalizeSpell(spell);
}
export async function getSpellsByClass(className, documentSlug = DEFAULT_DOCUMENT) {
    return getSpells({ documentSlug, className });
}
export async function getCantrips(documentSlug = DEFAULT_DOCUMENT) {
    return getSpells({ documentSlug, level: 0 });
}
// =============================================================================
// FEATS
// =============================================================================
export async function getFeats(documentSlug = DEFAULT_DOCUMENT) {
    const url = `${API_BASE_V1}/feats/?document__slug=${documentSlug}`;
    return fetchAllPages(url);
}
export async function getFeat(slug) {
    const url = `${API_BASE_V1}/feats/${slug}/`;
    return fetchJson(url);
}
// =============================================================================
// CONDITIONS
// =============================================================================
export async function getConditions(documentSlug = DEFAULT_DOCUMENT) {
    const url = `${API_BASE_V1}/conditions/?document__slug=${documentSlug}`;
    return fetchAllPages(url);
}
export async function getCondition(slug) {
    const url = `${API_BASE_V1}/conditions/${slug}/`;
    return fetchJson(url);
}
export async function getMonsters(filter = {}) {
    const params = new URLSearchParams();
    if (filter.documentSlug)
        params.set('document__slug', filter.documentSlug);
    else
        params.set('document__slug', DEFAULT_DOCUMENT);
    if (filter.type)
        params.set('type', filter.type);
    if (filter.cr !== undefined)
        params.set('cr', String(filter.cr));
    if (filter.crRange?.min !== undefined)
        params.set('cr__gte', String(filter.crRange.min));
    if (filter.crRange?.max !== undefined)
        params.set('cr__lte', String(filter.crRange.max));
    if (filter.search)
        params.set('search', filter.search);
    if (filter.limit)
        params.set('limit', String(filter.limit));
    const url = `${API_BASE_V1}/monsters/?${params.toString()}`;
    return fetchAllPages(url);
}
export async function getMonster(slug) {
    const url = `${API_BASE_V1}/monsters/${slug}/`;
    return fetchJson(url);
}
// =============================================================================
// EQUIPMENT
// =============================================================================
export async function getWeapons(documentSlug = DEFAULT_DOCUMENT) {
    const url = `${API_BASE_V1}/weapons/?document__slug=${documentSlug}`;
    return fetchAllPages(url);
}
export async function getArmor(documentSlug = DEFAULT_DOCUMENT) {
    const url = `${API_BASE_V1}/armor/?document__slug=${documentSlug}`;
    return fetchAllPages(url);
}
export async function getMagicItems(documentSlug = DEFAULT_DOCUMENT) {
    const url = `${API_BASE_V1}/magicitems/?document__slug=${documentSlug}`;
    return fetchAllPages(url);
}
export async function loadSrdData() {
    const cacheKey = 'srd:all';
    const cached = getCached(cacheKey);
    if (cached)
        return cached;
    // Fetch all in parallel
    const [races, classes, backgrounds, spells, feats, conditions] = await Promise.all([
        getRaces(),
        getClasses(),
        getBackgrounds(),
        getSpells({ limit: 500 }), // Get all spells
        getFeats(),
        getConditions(),
    ]);
    const data = { races, classes, backgrounds, spells, feats, conditions };
    setCache(cacheKey, data);
    return data;
}
//# sourceMappingURL=open5e-client.js.map